import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Avatar, Box, IconButton, Paper, Slide, Tooltip, Typography, alpha, useMediaQuery } from '@mui/material';
import CallEndRounded from '@mui/icons-material/CallEndRounded';
import CallRounded from '@mui/icons-material/CallRounded';
import MicRounded from '@mui/icons-material/MicRounded';
import MicOffRounded from '@mui/icons-material/MicOffRounded';
import OpenInFullRounded from '@mui/icons-material/OpenInFullRounded';
import { useSalaDeLlamada } from './useSalaDeLlamada';
import { PantallaDeLlamada } from './PantallaDeLlamada';
import { useAppSelector } from '../../hooks/useAppStore';
import { useFlagEstricta } from '../../hooks/useFeatureFlag';
import { getPusher } from '../../lib/pusher';
import { chatCallsApi, useGetActiveChatCallQuery, useHangupChatCallMutation, LLAMADA_ACTIVA } from '../../store/api/chatCallsApi';
import { ORO, VERDE, latido } from '../enVivo/estilo';

const AVATAR_CHICO = { width: 32, height: 32, fontSize: 13, fontWeight: 800, bgcolor: alpha(VERDE, 0.25), color: '#fff', border: '2px solid #0f1218', ml: -1 } as const;

/** "02:14" desde que atendieron. */
function reloj(desde: string | null, ahora: number) {
  if (!desde) return '';
  const s = Math.max(0, Math.floor((ahora - new Date(desde).getTime()) / 1000));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * LA LLAMADA DEL CHAT EN LA QUE ESTOY (fase 2, 14/10/2026). Vive en el layout, arriba al centro
 * como el timbre: "Llamando a…" con Cancelar mientras suena, y "En llamada · 02:14" con quiénes
 * están, micrófono y Cortar cuando atendieron. Tocarla abre la pantalla con audio y video (fase 3).
 *
 * Escucha `chat_call.updated` en `private-user-{id}`: cualquier cambio de una llamada de mis
 * charlas refresca la activa (y la que esté mirando un mensaje del chat).
 */
export function LlamadaDelChat() {
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const myId = useAppSelector((s) => s.auth.user?._id);
  const habilitado = useFlagEstricta('llamadas-chat');
  const dispatch = useDispatch();
  const { data: call } = useGetActiveChatCallQuery(undefined, { skip: !accessToken || !habilitado, refetchOnFocus: true, refetchOnReconnect: true });
  const [colgar, { isLoading: colgando }] = useHangupChatCallMutation();
  const [ahora, setAhora] = useState(Date.now());
  const menosMovimiento = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [abierta, setAbierta] = useState(false);
  const abiertaPara = useRef<string | null>(null);

  // Fase 3: el audio/video. Conecta mientras estoy adentro de una llamada viva (el que llama
  // conecta ya mientras suena: cuando el otro atiende, se escuchan al instante).
  const viva = !!call && (call.status === 'ringing' || call.status === 'ongoing');
  const sala = useSalaDeLlamada(viva ? call!._id : null);

  // Cuando atienden, la pantalla se abre sola una vez por llamada; minimizarla no corta.
  useEffect(() => {
    if (call?.status === 'ongoing' && abiertaPara.current !== call._id) { abiertaPara.current = call._id; setAbierta(true); }
    if (!viva) setAbierta(false);
  }, [call?.status, call?._id, viva]);

  useEffect(() => {
    if (!accessToken || !habilitado) return;
    let userId: string;
    try { userId = JSON.parse(atob(accessToken.split('.')[1])).sub as string; } catch { return; }
    const channel = getPusher(accessToken).subscribe(`private-user-${userId}`);
    const onUpdate = (d: { callId: string }) => {
      dispatch(chatCallsApi.util.invalidateTags([LLAMADA_ACTIVA, { type: 'ChatCall', id: d.callId }]));
    };
    channel.bind('chat_call.updated', onUpdate);
    return () => { channel.unbind('chat_call.updated', onUpdate); };
  }, [accessToken, habilitado, dispatch]);

  useEffect(() => {
    if (call?.status !== 'ongoing') return;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [call?.status]);

  const otros = call?.participants.filter((p) => p.user !== myId) ?? [];
  const adentro = otros.filter((p) => p.status === 'joined');
  const sonando = call?.status === 'ringing';
  const nombre = call ? (call.kind === 'group' ? call.title : (otros[0]?.name ?? call.title)) : '';
  const detalle = !call ? ''
    : sonando ? (call.kind === 'group' ? `Sonando a ${otros.filter((p) => p.status === 'ringing').length} del grupo` : 'Sonando…')
    : `${reloj(call.answeredAt, ahora)}${call.kind === 'group' ? ` · ${adentro.length + 1} en la llamada` : ''}`;
  const avisoSala = sala.estado === 'reconectando' ? 'Reconectando…' : sala.estado === 'sin-configurar' ? 'Sin audio: falta configurarlo' : sala.estado === 'error' ? 'Sin audio' : '';
  const titulo = sonando ? `Llamando a ${nombre}` : `En llamada con ${nombre}`;
  const cortar = () => { if (call) { setAbierta(false); void colgar(call._id); } };

  return (
    <>
    <Slide in={viva && !abierta} direction="down" mountOnEnter unmountOnExit appear>
      <Paper role="status" aria-live="polite" elevation={0}
        sx={{
          position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: (th) => th.zIndex.snackbar + 2,
          display: 'flex', alignItems: 'center', gap: 1.25, pl: 1, pr: 0.75, py: 0.75, borderRadius: 999,
          width: 'max-content', maxWidth: 'min(94vw, 520px)',
          bgcolor: alpha('#0f1218', 0.97), color: '#fff', border: `1px solid ${alpha(sonando ? ORO : VERDE, 0.55)}`,
          boxShadow: `0 14px 44px ${alpha('#000', 0.5)}`, backdropFilter: 'blur(8px)',
        }}>
        {call && (
          <>
            <Box onClick={() => setAbierta(true)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', minWidth: 0 }}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                {call.kind === 'group' && adentro.length ? (
                  // Superpuestos a mano: AvatarGroup de MUI revienta el chequeo de tipos (TS2590).
                  <Box sx={{ display: 'flex', pl: 1 }}>
                    {adentro.slice(0, 3).map((p) => <Avatar key={p.user} sx={AVATAR_CHICO}>{p.name.slice(0, 1)}</Avatar>)}
                  </Box>
                ) : (
                  <Avatar sx={{ width: 40, height: 40, fontSize: 15, fontWeight: 800, bgcolor: alpha(sonando ? ORO : VERDE, 0.2), color: '#fff', border: `1.5px solid ${alpha(sonando ? ORO : VERDE, 0.5)}`, animation: sonando && !menosMovimiento ? `${latido} 1.6s ease-out infinite` : 'none' }}>
                    {nombre.slice(0, 1)}
                  </Avatar>
                )}
              </Box>
              <Box sx={{ minWidth: 0, py: 0.25, mr: 0.5 }}>
                <Typography noWrap sx={{ fontWeight: 800, fontSize: 13.5, lineHeight: 1.2, color: sonando ? ORO : VERDE, maxWidth: 280 }}>
                  {titulo}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12, lineHeight: 1.3, color: alpha('#fff', 0.72), maxWidth: 280, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!sonando && !avisoSala && <CallRounded sx={{ fontSize: 12, color: VERDE }} />}{avisoSala || detalle}
                </Typography>
              </Box>
            </Box>
            {sala.estado === 'conectada' && (
              <Tooltip title={sala.micPrendido ? 'Silenciar' : 'Activar micrófono'}>
                <IconButton aria-label={sala.micPrendido ? 'Silenciar' : 'Activar micrófono'} aria-pressed={sala.micPrendido} onClick={() => sala.alternar('mic')}
                  sx={{ width: 40, height: 40, color: sala.micPrendido ? '#fff' : '#0f1218', bgcolor: sala.micPrendido ? alpha('#fff', 0.12) : '#fff', '&:hover': { bgcolor: sala.micPrendido ? alpha('#fff', 0.2) : '#fff' } }}>
                  {sala.micPrendido ? <MicRounded sx={{ fontSize: 20 }} /> : <MicOffRounded sx={{ fontSize: 20 }} />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Abrir la llamada">
              <IconButton aria-label="Abrir la llamada" onClick={() => setAbierta(true)} sx={{ width: 40, height: 40, color: '#fff', bgcolor: alpha('#fff', 0.12), '&:hover': { bgcolor: alpha('#fff', 0.2) } }}>
                <OpenInFullRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={sonando ? 'Cancelar' : 'Cortar'}>
              <span>
                <IconButton aria-label={sonando ? 'Cancelar la llamada' : 'Cortar'} disabled={colgando} onClick={cortar}
                  sx={{ width: 40, height: 40, color: '#fff', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, '&.Mui-disabled': { bgcolor: alpha('#dc2626', 0.5), color: '#fff' }, '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 } }}>
                  <CallEndRounded sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Paper>
    </Slide>
    {call && viva && (
      <PantallaDeLlamada abierta={abierta} onMinimizar={() => setAbierta(false)} onCortar={cortar} titulo={titulo} detalle={detalle} sala={sala} />
    )}
    </>
  );
}
